import type { CSSProperties } from "react"
import FlipClockCountdown from "@leenguyen/react-flip-clock-countdown"
import "@leenguyen/react-flip-clock-countdown/dist/index.css"
import "./tournamentFlip.css"

import { cn } from "@/shared/lib/utils"

/**
 * Retro flip-clock countdown reading days / hours / minutes / seconds.
 *
 * Themed to the Figma spec: white digit cards, black bold Roboto Condensed
 * glyphs, no labels or separators. `hideOnComplete={false}` keeps the clock
 * "stuck at zeros" once the target is reached (per design — show zeros, not a
 * completed state). `onComplete` lets callers react to the target passing
 * (e.g. switch from "until start" to "until end").
 *
 * IMPORTANT: sizing is driven through the library's own CSS variables, not
 * `digitBlockStyle`. The component splits each card into top/bottom halves
 * sized from `--fcc-digit-block-height`; overriding width/height/font-size via
 * inline `digitBlockStyle` desyncs that math and renders two clipped half-digits.
 */
type FlipVariant = "xs" | "sm" | "lg"

interface VariantSpec {
  blockWidth: number
  blockHeight: number
  fontSize: number
  radius: number
  clockSpacing: number
  digitSpacing: number
}

const VARIANTS: Record<FlipVariant, VariantSpec> = {
  // Leaderboard cover timer (compact).
  xs: { blockWidth: 16, blockHeight: 24, fontSize: 20, radius: 4, clockSpacing: 0, digitSpacing: 1 },
  // Welcome sheet timer.
  sm: { blockWidth: 24, blockHeight: 36, fontSize: 26, radius: 6, clockSpacing: 0, digitSpacing: 1 },
  // Rewards tournament cover timer — text-3xl/leading-8 per Figma.
  lg: { blockWidth: 22, blockHeight: 32, fontSize: 30, radius: 5, clockSpacing: 0, digitSpacing: 1 },
}

function buildVars(v: VariantSpec): CSSProperties {
  return {
    "--fcc-flip-duration": "0.6s",
    "--fcc-digit-block-width": `${v.blockWidth}px`,
    "--fcc-digit-block-height": `${v.blockHeight}px`,
    "--fcc-digit-block-radius": `${v.radius}px`,
    "--fcc-digit-block-spacing": `${v.digitSpacing}px`,
    "--fcc-spacing": `${v.clockSpacing}px`,
    "--fcc-digit-font-size": `${v.fontSize}px`,
    "--fcc-digit-color": "#18181b",
    "--fcc-background": "#FFFFFF",
    "--fcc-divider-color": "#000000",
    "--fcc-shadow": "inset 0.4px 0.4px 0.8px rgba(255, 255, 255, 0.25)",
  } as CSSProperties
}

/** Only typography here — never sizes (those go through CSS vars). */
const DIGIT_BLOCK_STYLE: CSSProperties = {
  fontFamily: "'Roboto Condensed', 'Arial Narrow', sans-serif",
  fontWeight: 700,
}

interface TournamentFlipCountdownProps {
  /** Target Unix ms timestamp to count down to. */
  to: number
  onComplete?: () => void
  variant?: FlipVariant
  /** Tint the seconds (last) unit purple, per the Rewards cover design. */
  accentLastUnit?: boolean
  className?: string
}

export function TournamentFlipCountdown({
  to,
  onComplete,
  variant = "sm",
  accentLastUnit = false,
  className,
}: TournamentFlipCountdownProps) {
  return (
    <FlipClockCountdown
      // Remount when the target changes so a mode switch re-seeds the clock.
      key={to}
      to={to}
      renderMap={[true, true, true, true]}
      showLabels={false}
      showSeparators={false}
      hideOnComplete={false}
      duration={0.6}
      digitBlockStyle={DIGIT_BLOCK_STYLE}
      onComplete={onComplete}
      className={cn(
        "flex items-center tfc-tight-sections",
        accentLastUnit && "tfc-accent-last",
        className
      )}
      style={buildVars(VARIANTS[variant])}
    />
  )
}
